import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_URL, APP_PREFIX_PATH } from "../config/AppConfig";
import Breadcrumbs from "../components/Breadcrumbs";
import { 
  FaCheckCircle, 
  FaTimesCircle, 
  FaCalendarAlt, 
  FaCreditCard, 
  FaInfinity, 
  FaBox, 
  FaSearch, 
  FaImage,
  FaBan,
  FaHistory,
  FaCrown,
  FaChevronDown,
  FaChevronUp,
  FaShoppingCart,
  FaClock,
  FaRegCalendarCheck
} from "react-icons/fa";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import "../assets/css/subscriptiondetails.scss";

const SubscriptionDetails = () => {
  const [allSubscriptions, setAllSubscriptions] = useState([]);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [historyPlans, setHistoryPlans] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const userId = sessionStorage.getItem("id");
  const token = sessionStorage.getItem("token");
  const navigate = useNavigate();

  const breadcrumbItems = [
    { label: "Home", path: `/${APP_PREFIX_PATH}/Home` },
    { label: "Subscription Details", path: `/${APP_PREFIX_PATH}/subcription` },
  ];

  useEffect(() => {
    fetchSubscription();
  }, [userId, token]);

  const fetchSubscription = async () => {
    if (!userId) {
      setError("User not found. Please login again.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(`${API_URL}/get_my_subscription`, {
        params: { user_id: userId },
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.data.success) {
        const subscriptionData = response.data.subscription_arr;
        
        if (subscriptionData && subscriptionData !== "NA" && subscriptionData.length > 0) {
          setAllSubscriptions(subscriptionData);
          
          const activePlan = subscriptionData.find(sub => sub.status === 0 || sub.status === "0");
          const historyPlans = subscriptionData.filter(sub => sub.status !== 0 && sub.status !== "0");
          
          setCurrentPlan(activePlan || null);
          setHistoryPlans(historyPlans);
        } else {
          setError("No subscription found");
        }
      } else {
        setError(response.data.msg || "No subscription found");
      }
    } catch (err) {
      console.error("Error fetching subscription:", err);
      setError("Unable to load subscription details. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return dateString;
  };

  const getDaysRemaining = (endDate) => {
    if (!endDate) return null;
    const end = new Date(endDate);
    const today = new Date();
    const diffTime = end - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  const getStatusBadge = (status) => {
    if (status === 0 || status === "0") {
      return (
        <span className="status-badge status-active">
          <FaCheckCircle /> Active
        </span>
      );
    } else if (status === 1 || status === "1") {
      return (
        <span className="status-badge status-expired">
          <FaClock /> Expired
        </span>
      );
    } else if (status === 2 || status === "2") {
      return (
        <span className="status-badge status-cancelled">
          <FaBan /> Cancelled
        </span>
      );
    }
    return (
      <span className="status-badge status-inactive">
        <FaTimesCircle /> Inactive
      </span>
    );
  };

  const getPaymentStatusBadge = (paymentStatus) => {
    if (paymentStatus === 1 || paymentStatus === "1") {
      return (
        <span className="status-badge status-paid">
          <FaCheckCircle /> Paid
        </span>
      );
    }
    return (
      <span className="status-badge status-pending">
        <FaClock /> Pending
      </span>
    );
  };

  const handleCancelSubscription = async () => {
    if (!currentPlan) return;

    const result = await Swal.fire({
      title: 'Cancel Subscription?',
      text: "Are you sure you want to cancel your subscription? This action cannot be undone.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, cancel it!',
      cancelButtonText: 'No, keep it',
      customClass: {
        popup: 'swal-custom-popup',
        confirmButton: 'swal-custom-confirm',
        cancelButton: 'swal-custom-cancel'
      }
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.post(
          `${API_URL}/cancel_subscription`,
          {
            user_id: userId,
            user_subscription_id: currentPlan.user_subscription_id
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.data.success) {
          Swal.fire({
            title: 'Cancelled!',
            text: 'Your subscription has been cancelled successfully.',
            icon: 'success',
            confirmButtonColor: '#28a745'
          });
          fetchSubscription();
        } else {
          Swal.fire({
            title: 'Error!',
            text: response.data.msg || 'Failed to cancel subscription.',
            icon: 'error',
            confirmButtonColor: '#dc3545'
          });
        }
      } catch (err) {
        console.error("Error cancelling subscription:", err);
        Swal.fire({
          title: 'Error!',
          text: 'Unable to cancel subscription. Please try again later.',
          icon: 'error',
          confirmButtonColor: '#dc3545'
        });
      }
    }
  };

  const toggleHistory = () => {
    setShowHistory(!showHistory);
  };

  if (loading) {
    return (
      <div className="subscription-page">
        <div className="container">
          <Breadcrumbs title="Subscription Details" items={breadcrumbItems} />
          <div className="loading-container">
            <div className="loading-spinner">
              <div className="spinner"></div>
            </div>
            <p className="loading-text">Loading your subscription details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !currentPlan && historyPlans.length === 0) {
    return (
      <div className="subscription-page">
        <div className="container">
          <Breadcrumbs title="Subscription Details" items={breadcrumbItems} />
          <div className="empty-state">
            <div className="empty-state-icon">
              <FaShoppingCart />
            </div>
            <h2>No Active Subscription</h2>
            <p>{error}</p>
            <p className="empty-state-subtext">
              Choose a plan that fits your needs and start enjoying all the benefits!
            </p>
            <button 
              className="btn-primary-custom"
              onClick={() => navigate(`/${APP_PREFIX_PATH}/subscription-plans`)}
            >
              <FaCrown className="btn-icon" />
              View Available Plans
            </button>
          </div>
        </div>
      </div>
    );
  }

  const daysRemaining = currentPlan ? getDaysRemaining(currentPlan.end_date) : null;

  return (
    <div className="subscription-page">
      <div className="container">
        <Breadcrumbs title="Subscription Details" items={breadcrumbItems} />

        {currentPlan && (
          <div className="current-subscription-section">
            {/* Hero Header */}
            <div className="subscription-hero">
              <div className="hero-content">
                <div className="hero-badge">
                  <FaCrown /> Premium Member
                </div>
                <h1 className="hero-title">{currentPlan.plan_name}</h1>
                <p className="hero-subtitle">{currentPlan.type_label}</p>
                <div className="hero-badges">
                  {getStatusBadge(currentPlan.status)}
                  {getPaymentStatusBadge(currentPlan.payment_status)}
                </div>
              </div>
              <div className="hero-price-card">
                <div className="price-label">Subscription Price</div>
                <div className="price-amount">
                  {currentPlan.amount === "0.00" ? "Free" : `AED ${currentPlan.amount}`}
                </div>
                <div className="price-period">{currentPlan.days} Days Plan</div>
                {(currentPlan.status === 0 || currentPlan.status === "0") && (
                  <button 
                    className="btn-cancel"
                    onClick={handleCancelSubscription}
                  >
                    <FaBan />
                    Cancel Subscription
                  </button>
                )}
              </div>
            </div>

            {/* Time Info Cards */}
            <div className="time-info-grid">
              <div className="time-card">
                <div className="time-icon start-icon">
                  <FaRegCalendarCheck />
                </div>
                <div className="time-content">
                  <div className="time-label">Start Date</div>
                  <div className="time-value">{formatDate(currentPlan.start_date)}</div>
                </div>
              </div>
              <div className="time-card">
                <div className="time-icon end-icon">
                  <FaCalendarAlt />
                </div>
                <div className="time-content">
                  <div className="time-label">End Date</div>
                  <div className="time-value">{formatDate(currentPlan.end_date)}</div>
                </div>
              </div>
              {daysRemaining !== null && daysRemaining >= 0 && (
                <div className="time-card highlight-card">
                  <div className="time-icon days-icon">
                    <FaClock />
                  </div>
                  <div className="time-content">
                    <div className="time-label">Days Remaining</div>
                    <div className="time-value highlight">{daysRemaining} Days</div>
                  </div>
                </div>
              )}
            </div>

            {/* Features Grid */}
            <div className="features-section">
              <h3 className="section-title">Plan Features & Limits</h3>
              <div className="features-grid">
                <div className="feature-card">
                  <div className="feature-icon products-icon">
                    <FaBox />
                  </div>
                  <div className="feature-content">
                    <div className="feature-label">Products</div>
                    <div className="feature-value">
                      {currentPlan.product_limit === 0 ? (
                        <>
                          <FaInfinity className="infinity-icon" /> Unlimited
                        </>
                      ) : (
                        currentPlan.product_limit
                      )}
                    </div>
                  </div>
                </div>
                <div className="feature-card">
                  <div className="feature-icon images-icon">
                    <FaImage />
                  </div>
                  <div className="feature-content">
                    <div className="feature-label">Images</div>
                    <div className="feature-value">
                      {currentPlan.image_limit === 0 ? (
                        <>
                          <FaInfinity className="infinity-icon" /> Unlimited
                        </>
                      ) : (
                        currentPlan.image_limit
                      )}
                    </div>
                  </div>
                </div>
                <div className="feature-card">
                  <div className="feature-icon searches-icon">
                    <FaSearch />
                  </div>
                  <div className="feature-content">
                    <div className="feature-label">Searches</div>
                    <div className="feature-value">
                      {currentPlan.search_limit === 0 ? (
                        <>
                          <FaInfinity className="infinity-icon" /> Unlimited
                        </>
                      ) : (
                        currentPlan.search_limit
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Subscription History */}
        {historyPlans.length > 0 && (
          <div className="history-section">
            <div 
              className={`history-toggle ${showHistory ? 'active' : ''}`}
              onClick={toggleHistory}
            >
              <div className="history-toggle-content">
                <FaHistory className="history-icon" />
                <h3>Subscription History</h3>
                <span className="history-count">{historyPlans.length}</span>
              </div>
              <div className="toggle-icon">
                {showHistory ? <FaChevronUp /> : <FaChevronDown />}
              </div>
            </div>

            {showHistory && (
              <div className="history-list">
                {historyPlans.map((sub) => (
                  <div key={sub.user_subscription_id} className="history-card">
                    <div className="history-header">
                      <div className="history-info">
                        <h4 className="history-plan-name">
                          {sub.plan_name}
                          {sub.type === "Paid" && <FaCrown className="crown-icon" />}
                        </h4>
                        <div className="history-badges-container">
                          {getStatusBadge(sub.status)}
                          {getPaymentStatusBadge(sub.payment_status)}
                        </div>
                      </div>
                      <div className="history-price-info">
                        <div className="history-price">
                          {sub.amount === "0.00" ? "Free" : `AED ${sub.amount}`}
                        </div>
                        <div className="history-duration">{sub.days} Days</div>
                      </div>
                    </div>

                    <div className="history-details">
                      <div className="history-dates">
                        <div className="date-item">
                          <FaCalendarAlt className="date-icon" />
                          <div>
                            <div className="date-label">Started</div>
                            <div className="date-value">{formatDate(sub.start_date)}</div>
                          </div>
                        </div>
                        <div className="date-item">
                          <FaCalendarAlt className="date-icon" />
                          <div>
                            <div className="date-label">Ended</div>
                            <div className="date-value">{formatDate(sub.end_date)}</div>
                          </div>
                        </div>
                      </div>

                      <div className="history-features">
                        <div className="history-feature-item">
                          <FaBox />
                          <span>{sub.product_limit === 0 ? "Unlimited" : sub.product_limit} Products</span>
                        </div>
                        <div className="history-feature-item">
                          <FaImage />
                          <span>{sub.image_limit === 0 ? "Unlimited" : sub.image_limit} Images</span>
                        </div>
                        <div className="history-feature-item">
                          <FaSearch />
                          <span>{sub.search_limit === 0 ? "Unlimited" : sub.search_limit} Searches</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionDetails;